const pages = new Set(['/', '/create', '/library', '/me', '/rewards', '/creator/insights', '/pricing', '/billing', '/subscription', '/about', '/feed', '/discover', '/music', '/studio', '/extension', '/guides', '/lyrics-generator', '/rap-lyrics-generator', '/album-cover-generator', '/mv-generator', '/clips', '/vocal-separation']);
export function analyticsPath(value) {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return '/other';
  const path = value.split(/[?#]/)[0].replace(/^\/(?:en|zh|zh-TW|ja|ko|fr|de|es)(?=\/|$)/, '').replace(/\/$/, '') || '/';
  for (const prefix of ['share', 'invite', 'song', 'checkout', 'payment', 'guides']) if (path.startsWith(`/${prefix}/`)) return `/${prefix}/:id`;
  return pages.has(path) ? path : '/other';
}
const enumFields = {
  source: ['share','direct','web','musicmint_extension','pricing','library','create','feed'],
  destination: ['app_store','web','create','pricing'],
  cta_location: ['top','hero','footer','about','end','post_create','banner','inapp','sticky','header','inline'],
  mode: ['normal','simple','advanced','custom','basic','instrumental','lyrics'],
  action: ['app_store','compose','create','share','copy','download','upgrade'],
  method: ['clerk'],
  ai_referrer: ['chatgpt','perplexity','claude','gemini','copilot','poe','you','phind','deepseek','grok','ai-bot'],
};
const booleans = new Set(['has_gift','has_lyrics','is_ios','is_logged_in','is_vip']);
export const campaignNames = new Set(['web_footer','web_about','invite_landing', ...['top','hero','end','banner','inapp','sticky','footer'].flatMap(place => [`share_${place}`, `share_gift_${place}`])]);
export function sanitizeAnalyticsParams(input = {}) {
  const output = {};
  for (const [key, value] of Object.entries(input || {})) {
    if (key === 'page_path') output[key] = analyticsPath(value);
    else if (booleans.has(key) && typeof value === 'boolean') output[key] = value;
    else if (Object.hasOwn(enumFields, key) && enumFields[key].includes(value)) output[key] = value;
    else if (key === 'campaign_token' && campaignNames.has(value)) output[key] = value;
  }
  return output;
}
/** No pre-consent history. Only post-consent events may wait for the script. */
export function createAnalyticsDispatcher({ send, enabled = true, now = Date.now, limit = 50, ttl = 300000 }) {
  let consent = 'unknown', ready = false, queue = [], lastPage = '', sequence = 0;
  const delivered = new Set();
  function flush() {
    if (!enabled || consent !== 'granted' || !ready) return;
    const pending = queue; queue = [];
    for (const item of pending) {
      if (now() - item.at > ttl || delivered.has(item.key)) continue;
      try { send(item.name, item.params); delivered.add(item.key); } catch { /* Analytics never blocks product actions. */ }
      if (delivered.size > 200) delivered.delete(delivered.values().next().value);
    }
  }
  function track(name, params = {}, key) {
    if (!enabled || consent !== 'granted' || typeof name !== 'string' || !/^[a-z][a-z0-9_]{0,39}$/.test(name)) return false;
    const itemKey = key || `event:${++sequence}`;
    if (delivered.has(itemKey) || queue.some(item => item.key === itemKey)) return false;
    queue.push({ name, params: sanitizeAnalyticsParams(params), key: itemKey, at: now() });
    if (queue.length > limit) queue.shift();
    flush(); return true;
  }
  return {
    track,
    setReady(value) { ready = value === true; flush(); },
    setConsent(value) {
      const next = ['granted','denied'].includes(value) ? value : 'unknown';
      if (next !== consent) { queue = []; lastPage = ''; }
      consent = next; flush();
    },
    page(path) {
      const clean = analyticsPath(path);
      if (consent !== 'granted' || clean === lastPage) return;
      if (track('page_view', {page_path: clean})) lastPage = clean;
    },
    snapshot: () => ({consent, ready, queued: queue.length}),
  };
}
export function appStoreUrl(campaign, provider = '') {
  const ct = campaignNames.has(campaign) ? campaign : 'web_footer';
  const query = new URLSearchParams({ct, mt:'8'});
  if (/^\d{1,20}$/.test(provider)) query.set('pt', provider);
  return `https://apps.apple.com/app/id6737772961?${query}`;
}
export function appStoreAffiliate(campaign, provider = '') {
  return new URL(appStoreUrl(campaign, provider)).search.slice(1);
}
/** Receipt IDs stay in this tab; only fixed low-cardinality fields leave it. */
export function createSuccessTracker({storage, emit, now = Date.now}) {
  const key = 'musicmint:analytics-success:v1';
  const read = () => { try { const rows = JSON.parse(storage().getItem(key) || '[]'); return Array.isArray(rows) ? rows.filter(row => typeof row?.key === 'string' && Number.isFinite(row.at) && now() - row.at < 86400000).slice(-100) : []; } catch { return []; } };
  function write(rows) { try { storage().setItem(key, JSON.stringify(rows.slice(-100))); return true; } catch { return false; } }
  return {
    remember(owner, task, source = 'direct') {
      if (!owner || typeof task !== 'string' || !task || task.length > 256) return;
      const rows = read(), id = `generation:${owner}:${task}`;
      if (!rows.some(row => row.key === id)) write([...rows, {key:id, owner, task, source:source === 'share' ? 'share' : 'direct', at:now(), done:false}]);
    },
    complete(owner, task) {
      if (!owner || task?.status !== 3 || !task?.music_data?.some(song => song.id && (song.audio_url || song.audio_oss))) return;
      const rows = read(), row = rows.find(row => row.key === `generation:${owner}:${task.id}` && !row.done);
      if (!row) return;
      row.done = true;
      if (write(rows)) emit('generate_success', {source:row.source, page_path:'/create'});
    },
    signup(userId, sessionId, signup) {
      if (!userId || !sessionId || signup?.status !== 'complete' || signup.createdUserId !== userId || signup.createdSessionId !== sessionId) return;
      const rows = read(), id = `signup:${userId}`;
      if (rows.some(row => row.key === id)) return;
      if (write([...rows, {key:id, at:now(), done:true}])) emit('sign_up', {method:'clerk'});
    },
  };
}
